<script>

import {defineComponent} from "vue";
import {GravyPath} from "@/path";
import axios from "axios";
import Swal from "sweetalert2";
import moment from "moment";

// Mirrors markAsShipped.vue, but captures the DELIVERED quantity. Each line's
// qty_delivered defaults to its qty_shipped (what was sent); ops can adjust it
// down (short delivery) or up (excess delivery) to the actual received quantity.
export default defineComponent({
  props : ['selectedOrder'],
  computed: {
    GravyPath() {
      return GravyPath
    }
  },
  data(){
    return {
      form : {
        gravies : [],
        packagings : []
      },
      baseUrl : process.env.VUE_APP_API_BACKEND_URL,
      fields : ['name','qty_shipped','qty_delivered'],
      filter : '',
      // True when the outlet has already submitted a GRN for this order.
      hasGrn : false,
      // When that GRN was submitted, shown in the banner.
      grnAt : null,
      // Blocks a second submit while the first is still in flight.
      saving : false
    }
  },
  mounted() {
    if(Object.keys(this.selectedOrder).length > 0){
      // Backend looks the order up by order_id (see gravyMarkAsDelivered).
      this.form.order_id = this.selectedOrder.id;
      this.form.special_instruction = this.selectedOrder.special_instruction;
      // Read straight off the order, NOT inside the items call: if that call fails
      // this would stay false and the modal would claim "GRN not done yet" for an
      // order that actually has one.
      this.hasGrn = this.selectedOrder.grn_submitted_at != null;
      this.grnAt = this.selectedOrder.grn_submitted_at || null;
      this.getOrderDetails();
    }
  },
  methods : {
    // Same format as the orders list (index.vue).
    getFormatedDate(v){
      return moment(v).format("YYYY-MM-DD");
    },
    async getOrderDetails(){
      try{
        let res = await axios.get(this.baseUrl + GravyPath.ORDERS.ORDER_ITEMS,{
          params : {
            order_id : this.selectedOrder.id
          }
        });
        if(res.data.status){
          // The list row this modal was opened from can be minutes old, so the GRN
          // state is re-read from the items response (every line carries the order's
          // order_grn_submitted_at). Must happen BEFORE the mapping below, which
          // reads hasGrn.
          const fresh = res.data.data && res.data.data.length ? res.data.data[0].order_grn_submitted_at : undefined;
          if(fresh !== undefined){
            this.hasGrn = fresh != null;
            this.grnAt = fresh || null;
          }
          // hasGrn is set in mounted(). With a GRN, a qty_delivered of 0 is a real
          // count (nothing received) rather than "no GRN yet", so it must not fall
          // back to qty_shipped.
          const delivered = (f) => this.hasGrn
            ? (Number(f.qty_delivered) || 0)
            : ((f.qty_delivered && !isNaN(f.qty_delivered)) ? f.qty_delivered : f.qty_shipped);
          // item_type is stamped because the row IS the gravy/packaging object once
          // flattened, and the two carry their pack size on opposite columns.
          this.form.gravies = res.data.data.filter(f => f.gravy != null).map((f) => {
            let g = f.gravy;
            g.item_type = 'gravy';
            g.qty_shipped = f.qty_shipped;
            g.qty_delivered = delivered(f);
            g.batch_code = f.batch_code;
            return g;
          });
          this.form.packagings = res.data.data.filter(f => f.packaging != null).map((f) => {
            let g = f.packaging;
            g.item_type = 'packaging';
            g.qty_shipped = f.qty_shipped;
            g.qty_delivered = delivered(f);
            g.batch_code = f.batch_code;
            return g;
          });
        }else{
          // The API reports failures as HTTP 200 + status:false, so the catch below
          // never sees them. Without this the modal opens empty and ops would confirm
          // a delivery against a list that never loaded.
          Swal.fire(res.data.message || "Could not load the order items","","error");
        }
      }catch (e) {
        console.log(e);
        Swal.fire("Could not load the order items","","error");
      }
    },
    // qty_delivered is a FLOAT, same as qty_shipped, so a line can legitimately be
    // fractional (0.2 of a pack).
    //
    // The steppers mutate the row object `v` directly instead of looking it up by id.
    // The old findIndex matched on id and short-circuited its batch_code guard, so on
    // an order carrying the same product twice it always hit the FIRST row — clicking
    // + on row 2 bumped row 1. b-table hands back the very object held in form.*,
    // so writing to `v` always updates the row ops actually clicked.
    //
    // Typing is left to v-model alone: clamping on every keystroke made a decimal
    // impossible to enter ("1." parsed to 1, so "1.5" became 15). Values are
    // sanitised once, at submit.
    addQty(v){
      let c = parseFloat(v.qty_delivered);
      if(isNaN(c)){
        c = 0;
      }
      v.qty_delivered = Number((c + 1).toFixed(6));
    },
    minusQty(v){
      let c = parseFloat(v.qty_delivered);
      if(isNaN(c)){
        c = 0;
      }
      if(c - 1 < 0){
        return;
      }
      v.qty_delivered = Number((c - 1).toFixed(6));
    },
    addPQty(v){
      let c = parseFloat(v.qty_delivered);
      if(isNaN(c)){
        c = 0;
      }
      v.qty_delivered = Number((c + 1).toFixed(6));
    },
    minusPQty(v){
      let c = parseFloat(v.qty_delivered);
      if(isNaN(c)){
        c = 0;
      }
      if(c - 1 < 0){
        return;
      }
      v.qty_delivered = Number((c - 1).toFixed(6));
    },
    // Delivered minus shipped, in packs. Negative = short, positive = excess.
    diff(v){
      return Number((this.cleanQty(v.qty_delivered) - (parseFloat(v.qty_shipped) || 0)).toFixed(6));
    },
    // Pack size and loose unit sit on opposite columns for the two types:
    // gravy = uom x units (1 pc = 500 gm), packaging = units x uom (1 pack = 24 pcs).
    packSize(v){
      const n = Number(v.item_type === 'gravy' ? v.uom : v.units);
      return n > 0 ? n : 1;
    },
    looseUnit(v){
      return (v.item_type === 'gravy' ? v.units : v.uom) || '';
    },
    // "1 pc = 500 gm" / "1 pack = 24 pcs" — same wording as the partner GRN screen,
    // so the number in the Qty columns (which is in PACKS) can be read.
    unitLabel(v){
      const size = this.packSize(v), unit = this.looseUnit(v);
      if(size <= 1 || !unit){
        return '';
      }
      return (unit !== 'pcs' ? '1 pc = ' : '1 pack = ') + size + ' ' + unit;
    },
    // Short/excess in the unit the outlet counted in rather than in packs: 0.083
    // packs of a 120-pcs item is 10 pcs, which is what they actually handed over.
    // Pieces can only be whole; weights keep 2dp.
    diffBase(v){
      const d = this.diff(v) * this.packSize(v);
      return this.looseUnit(v) === 'pcs' ? Math.round(d) : Number(d.toFixed(2));
    },
    // Single sanitising point for everything typed: non-numeric or negative -> 0,
    // capped at 6dp to match what the API stores. NOT 3dp: this runs over every
    // row at submit, so a GRN value of 0.083333 (10 pcs of a 120-pcs pack) would be
    // trimmed back to 0.083 just by confirming the delivery.
    cleanQty(q){
      let n = parseFloat(q);
      if(isNaN(n) || n < 0){
        return 0;
      }
      return Number(n.toFixed(6));
    },
    async markAsDelivered(){
      // The API answers failures with HTTP 200 + status:false, so the catch block
      // alone never sees them. Both paths must report, otherwise a failed delivery
      // looks identical to nothing happening and ops just clicks again.
      if(this.saving){
        return;
      }
      this.saving = true;
      try{
        this.form.gravies.forEach(g => { g.qty_delivered = this.cleanQty(g.qty_delivered); });
        this.form.packagings.forEach(p => { p.qty_delivered = this.cleanQty(p.qty_delivered); });
        let res = await axios.post(this.baseUrl + GravyPath.ORDERS.MARK_AS_DELIVERED,this.form);
        if(res.data.status){
          Swal.fire("success","","success");
          this.$bvModal.hide('bv-mark-as-delivered');
          this.$emit('update');
        }else{
          Swal.fire(res.data.message || "Failed to mark as delivered","","error");
        }
      }catch (e) {
        console.log(e);
        Swal.fire((e.response && e.response.data && e.response.data.message) || e.message || "Failed to mark as delivered","","error");
      }finally{
        this.saving = false;
      }
    }
  }
})
</script>

<template>
  <div>
    <b-row>
      <b-col sm="6">
        <label>Restaurant</label>
        <p>{{this.selectedOrder.restaurant.name}}</p>
      </b-col>
      <b-col sm="6">
        <label>Special Instruction</label>
        <p>{{form.special_instruction}}</p>
      </b-col>
    </b-row>
    <b-alert :show="hasGrn" variant="success" class="py-2">
      <strong>GRN done</strong> — counted by the outlet<span v-if="grnAt"> on {{ getFormatedDate(grnAt) }}</span>.
    </b-alert>
    <b-alert :show="!hasGrn" variant="warning" class="py-2">
      <strong>No GRN</strong> — the outlet has not counted this order. The quantities
      below are what was shipped.
    </b-alert>
    <b-card no-body>
      <b-tabs card>
        <b-tab title="Gravies">
          <div class="table-responsive mb-0">
            <b-table
                :key="form.gravies.key"
                :items="form.gravies"
                :fields="fields"
                responsive="sm"
                :filter="filter"
            >
              <!-- Short/excess sits with the item name rather than in its own column,
                   so the line reads in one go. -->
              <template v-slot:cell(name)="row">
                {{ row.item.name }}
                <b-badge v-if="diff(row.item) < 0" variant="danger" class="ml-1">Short {{ Math.abs(diffBase(row.item)) }} {{ looseUnit(row.item) }}</b-badge>
                <b-badge v-else-if="diff(row.item) > 0" variant="warning" class="ml-1">Excess {{ diffBase(row.item) }} {{ looseUnit(row.item) }}</b-badge>
                <div v-if="unitLabel(row.item)" class="text-muted small">{{ unitLabel(row.item) }}</div>
              </template>
              <template v-slot:cell(qty_delivered)="row">
                <!-- fixed width instead of a col-* wrapper: the grid column squeezed
                     the input between the -/+ buttons until the number was unreadable -->
                <div style="width: 160px">
                  <b-input-group>
                    <b-input-group-prepend>
                      <b-button @click="minusQty(row.item)" size="sm"><i class="fa fa-minus"></i></b-button>
                    </b-input-group-prepend>
                    <b-form-input v-model="row.item.qty_delivered" type="text" inputmode="decimal" class="text-center" style="min-width: 70px"></b-form-input>
                    <b-input-group-append>
                      <b-button @click="addQty(row.item)" size="sm"><i class="fa fa-plus"></i></b-button>
                    </b-input-group-append>
                  </b-input-group>
                </div>
              </template>
            </b-table>
          </div>
        </b-tab>
        <b-tab title="Packaging">
          <div class="card-body">
            <div class="table-responsive mb-0">
              <b-table
                  :key="form.packagings.key"
                  :items="form.packagings"
                  :fields="fields"
                  responsive="sm"
                  :filter="filter"
              >
                <template v-slot:cell(name)="row">
                  {{ row.item.name }}
                  <b-badge v-if="diff(row.item) < 0" variant="danger" class="ml-1">Short {{ Math.abs(diffBase(row.item)) }} {{ looseUnit(row.item) }}</b-badge>
                  <b-badge v-else-if="diff(row.item) > 0" variant="warning" class="ml-1">Excess {{ diffBase(row.item) }} {{ looseUnit(row.item) }}</b-badge>
                  <div v-if="unitLabel(row.item)" class="text-muted small">{{ unitLabel(row.item) }}</div>
                </template>
                <template v-slot:cell(qty_delivered)="row">
                  <div style="width: 160px">
                    <b-input-group>
                      <b-input-group-prepend>
                        <b-button @click="minusPQty(row.item)" size="sm"><i class="fa fa-minus"></i></b-button>
                      </b-input-group-prepend>
                      <b-form-input type="text" inputmode="decimal" v-model="row.item.qty_delivered" class="text-center" style="min-width: 70px"></b-form-input>
                      <b-input-group-append>
                        <b-button @click="addPQty(row.item)" size="sm"><i class="fa fa-plus"></i></b-button>
                      </b-input-group-append>
                    </b-input-group>
                  </div>
                </template>
              </b-table>
            </div>
          </div>
        </b-tab>
      </b-tabs>
      <b-card-footer>
        <b-button variant="success" :disabled="saving" @click="markAsDelivered">{{ saving ? 'Saving...' : 'Mark As Delivered' }}</b-button>
      </b-card-footer>
    </b-card>
  </div>
</template>

<style>

</style>
